// src/Component/Project/paymentUtils.js
// Shared helpers so the Payment modals, the Project Details summary and the
// Dashboard all read a workstage's payment from the SAME place.
//
// Background: for Submission workstages the payment modal saves to
// workstage.paymentData, but older records (and the old summary code) keep it
// inside each submission_data[].paymentData. These helpers understand both.

const num = (v) => Number(v) || 0;
const r2 = (v) => Number(Number(v || 0).toFixed(2));
const pct = (part, whole) => (whole > 0 ? Number(((part / whole) * 100).toFixed(4)) : 0);

export const AMOUNT_KEYS = [
  "billingClaimedAmount",
  "billingReceivedAmount",
  "totalDeductionAmount",
  "balanceToClaimAmount",
  "balanceToReceiveAmount",
];

// any amount at all (incl. balances)
export const hasAnyPayment = (pd) => !!pd && AMOUNT_KEYS.some((k) => num(pd[k]) > 0);

// a "real" entry = something was actually claimed / received / deducted
export const hasRealEntry = (pd) =>
  !!pd &&
  (num(pd.billingClaimedAmount) > 0 ||
    num(pd.billingReceivedAmount) > 0 ||
    num(pd.totalDeductionAmount) > 0);

// The old Submission modal saved the retention % under the key "Percent"
// (typo). Read it back as retentionPercent; if it is missing, derive it.
export const normalizePaymentData = (pd) => {
  if (!pd) return {};
  const out = { ...pd };
  if (!(num(out.retentionPercent) > 0)) {
    if (num(pd.Percent) > 0) out.retentionPercent = num(pd.Percent);
    else if (num(pd.retentionAmount) > 0 && num(pd.billingReceivedAmount) > 0)
      out.retentionPercent = pct(num(pd.retentionAmount), num(pd.billingReceivedAmount));
  }
  return out;
};

// Which paymentData object(s) count for a workstage (used by summaries).
// Workstage-level data wins (it is what the modal saves today);
// submission-level data is the fallback. Never both → no double counting.
export const getPaymentSources = (ws) => {
  if (!ws) return [];
  if (ws.workstage_inputtype === "Submission") {
    if (hasRealEntry(ws.paymentData))
      return [{ label: "Workstage payment", pd: ws.paymentData }];
    const subs = (ws.submission_data || [])
      .filter((s) => hasAnyPayment(s.paymentData))
      .map((s) => ({ label: s.submissionName || "Unnamed Submission", pd: s.paymentData }));
    if (subs.length) return subs;
    if (hasAnyPayment(ws.paymentData))
      return [{ label: "Workstage payment", pd: ws.paymentData }];
    return [];
  }
  return hasAnyPayment(ws.paymentData) ? [{ label: null, pd: ws.paymentData }] : [];
};

// Combine every submission's payment into ONE workstage-level object
// (amounts summed, percentages re-derived from the sums).
export const mergeSubmissionPayments = (submissions, aboutToBill) => {
  const list = (submissions || []).map((s) => s.paymentData).filter(hasAnyPayment);
  if (!list.length) return null;
  const sum = (k) => r2(list.reduce((a, pd) => a + num(pd[k]), 0));

  const claimed = sum("billingClaimedAmount");
  const received = sum("billingReceivedAmount");
  const tds = sum("tdsAmount");
  const retention = sum("retentionAmount");
  const cess = sum("LabourcessAmount");
  const other = sum("otherDeductionAmount");
  const totalDeduction = sum("totalDeductionAmount") || r2(tds + retention + cess + other);

  return {
    billingClaimedAmount: claimed,
    billingClaimedPercent: pct(claimed, aboutToBill),
    billingReceivedAmount: received,
    billingReceivedPercent: pct(received, claimed),
    tdsAmount: tds,
    tdsPercent: pct(tds, received),
    retentionAmount: retention,
    retentionPercent: pct(retention, received),
    LabourcessAmount: cess,
    LabourcessPercent: pct(cess, received),
    otherDeductionAmount: other,
    otherDeductionPercent: pct(other, received),
    totalDeductionAmount: totalDeduction,
    netReceivedAmount: r2(received - totalDeduction),
    remarks: list.map((p) => p.remarks).filter(Boolean).join("; "),
  };
};

// What the Payment modal should show when it opens for a workstage.
export const getModalPayment = (ws) => {
  const own = normalizePaymentData(ws?.paymentData);
  if (!ws || ws.workstage_inputtype !== "Submission" || hasRealEntry(ws.paymentData)) return own;

  const aboutToBill = (ws.submission_data || [])
    .filter((s) => s.status === "completed")
    .reduce((a, s) => a + num(s.cost), 0);
  const merged = mergeSubmissionPayments(ws.submission_data, aboutToBill);
  return merged ? { ...own, ...merged } : own;
};

// ─── Deduction basis (Basic Value / GST / Total Amount) ──────────────────────
// Each deduction (TDS, Retention, Labour Cess, Other) can be calculated on the
// Basic Value part, the GST part, or the Total Amount of the payment received.
// The chosen basis is stored in paymentData as tdsBasis, retentionBasis,
// LabourcessBasis, otherDeductionBasis.

export const DEDUCTION_BASIS_OPTIONS = [
  { value: "basic_value", label: "Basic Value" },
  { value: "gst", label: "GST" },
  { value: "total_amount", label: "Total Amount" },
];

export const DEFAULT_DEDUCTION_BASIS = "total_amount";

export const getBasisLabel = (basis) =>
  DEDUCTION_BASIS_OPTIONS.find((o) => o.value === basis)?.label || "Total Amount";

// true when the project's Basic/GST split has actually been entered
export const hasCostBreakup = (cost) =>
  num(cost?.basic_value) > 0 || num(cost?.gst_amount) > 0;

// amount = approved/received amount of this payment
// cost   = { basic_value, gst_amount, total_amount } from ProjectDetails
// No basis saved, "Total Amount", or no breakup entered -> old behaviour
// (so existing records and calculations do not change).
export const getDeductionBase = (amount, basis, cost = {}) => {
  const basic = num(cost?.basic_value);
  const gst = num(cost?.gst_amount);
  const total = num(cost?.total_amount) || basic + gst;

  if (!basis || basis === "total_amount" || !total) return amount;
  if (basis === "basic_value") return (amount * basic) / total;
  if (basis === "gst") return (amount * gst) / total;
  return amount;
};